import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { pathEq, propEq } from 'ramda';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';

import { findAndReplace } from 'utils/functions';
import { isConfirmed } from 'utils/booking';
import { bookingShape, legsShape, nextAvailableTrainShape, userShape } from 'constants/propTypes';
import TrainMap from 'components/shared/TrainMap';
import SeatMap from 'components/shared/SeatMap';
import UpdatePassengerSeatsMutation from 'components/mutations/UpdatePassengerSeatsMutation';
import Checkbox from 'components/ui/Checkbox';
import { ConfirmButton, ActionButton, CancelButton } from 'components/ui/Button';
import { directions } from 'constants/booking';
import { types } from 'types/passengers';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import SeatMapLegend from 'components/ui/SeatMapLegend/SeatMapLegend';
import UnavailableSeat from '../SeatMap/UnavailableSeat';
import { shouldFetchBookedAssistancesPerCoaches } from 'utils/shouldFetchBookedAssistancesPerCoaches';

import {
  MoveContainer,
  MoveActionsContainer,
  MoveButtonsContainer,
  MoveCheckboxesContainer,
  ErrorMessage,
  UpdateSeatsContainer,
  StyledTabContainer,
  StyledTab,
  LegRoute,
  LegRouteOrigin,
  LegRouteDestination,
  LegDate,
  LegTime
} from './SeatSelection.styles';
import {
  findSeatsToUpdate,
  pickSeats,
  findSelectedLeg,
  findSelectedTrainSeats,
  findSelectedPassengersSegments,
  getCurrentInventoryClasses
} from './SeatSelection.utils';

import { isEurostarOrganization } from 'utils/user';

const getInitialState = ({
  outbound,
  inbound,
  isNewBooking,
  legId: initialLegId,
  visibleLegIds,
  isMissedDeparture,
  isFlowForwardCapped,
  nextAvailableTrain,
  selectedPassengerIds
}) => {
  const seats = pickSeats(
    { outbound, inbound },
    {
      isNewBooking,
      isMissedDeparture,
      isFlowForwardCapped,
      nextAvailableTrain,
      selectedPassengerIds
    }
  );

  const legId =
    initialLegId ||
    outbound.find(({ products }) => products.some(({ provisional }) => provisional))?.id ||
    inbound.find(({ products }) => products.some(({ provisional }) => provisional))?.id ||
    outbound[0]?.id ||
    inbound[0]?.id;

  const selectedLeg = findSelectedLeg(legId, seats);

  return {
    initialLegId,
    legId,
    visibleLegIds,
    isInbound: inbound.length > 0 && outbound.length === 0,
    seating: {
      originalSeats: seats,
      newSeats: seats,
      selectedPassenger: null,
      selectedPassengers: [],
      tempSeats: [],
      tempReversedSeats: [],
      tempCoach: null,
      tempLegId: null,
      selectedTempSeat: null,
      isReversed: false
    },
    showUnavailable: false,
    seatStatus: {},
    currentCoach: selectedLeg?.seats[0]?.coach,
    isMoveAll: false,
    isMoveBatch: false,
    currentInventoryClasses: getCurrentInventoryClasses(selectedLeg?.seats),
    seatsWithBookings: [],
    seatsWithoutBookings: []
  };
};

const getSeatMapRelevantProps = ({
  outbound,
  inbound,
  isNewBooking,
  isMissedDeparture,
  isFlowForwardCapped,
  nextAvailableTrain,
  selectedPassengerIds
}) => ({
  outbound,
  inbound,
  isNewBooking,
  isMissedDeparture,
  isFlowForwardCapped,
  nextAvailableTrain,
  selectedPassengerIds
});

const SeatSelection = (props) => {
  const {
    outbound,
    inbound,
    isNewBooking,
    isMissedDeparture,
    isFlowForwardCapped,
    nextAvailableTrain,
    selectedPassengerIds,
    bookingData: { passengers }
  } = props;

  const [state, setState] = useState(() => getInitialState(props));
  const stateRef = useRef(state);
  const previousPropsRef = useRef(null);

  stateRef.current = state;

  const updateSeatMap = useCallback(() => {
    const seats = pickSeats(
      { outbound, inbound },
      {
        isNewBooking,
        isMissedDeparture,
        isFlowForwardCapped,
        nextAvailableTrain,
        selectedPassengerIds
      }
    );

    setState((currentState) => ({
      ...currentState,
      seating: {
        ...currentState.seating,
        originalSeats: seats,
        newSeats: seats
      }
    }));
  }, [outbound, inbound, isNewBooking, isMissedDeparture, isFlowForwardCapped, nextAvailableTrain, selectedPassengerIds]);

  useEffect(() => {
    const currentRelevantProps = getSeatMapRelevantProps({
      outbound,
      inbound,
      isNewBooking,
      isMissedDeparture,
      isFlowForwardCapped,
      nextAvailableTrain,
      selectedPassengerIds
    });
    const prevProps = previousPropsRef.current;

    if (
      prevProps &&
      (prevProps.isNewBooking !== isNewBooking ||
        prevProps.isMissedDeparture !== isMissedDeparture ||
        prevProps.isFlowForwardCapped !== isFlowForwardCapped ||
        JSON.stringify(prevProps.outbound) !== JSON.stringify(outbound) ||
        JSON.stringify(prevProps.inbound) !== JSON.stringify(inbound) ||
        JSON.stringify(prevProps.nextAvailableTrain) !== JSON.stringify(nextAvailableTrain) ||
        JSON.stringify(prevProps.selectedPassengerIds) !== JSON.stringify(selectedPassengerIds))
    ) {
      updateSeatMap();
    }

    previousPropsRef.current = currentRelevantProps;
  }, [outbound, inbound, isNewBooking, isMissedDeparture, isFlowForwardCapped, nextAvailableTrain, selectedPassengerIds, updateSeatMap]);

  const onSelectNewSeat = (legId) => (seatNumber) => {
    setState((currentState) => {
      const {
        currentCoach,
        seating,
        seating: { newSeats, selectedPassenger }
      } = currentState;

      const seatsAfterChange = directions.reduce((acc, direction) => {
        const leg = newSeats[direction].find(({ id }) => id === legId);

        if (leg) {
          return {
            ...acc,
            [direction]: findAndReplace(propEq('id', legId), {
              id: legId,
              seats: findAndReplace(pathEq(['passenger', 'id'], selectedPassenger.id), {
                number: seatNumber,
                coach: currentCoach
              })(leg.seats)
            })(acc[direction])
          };
        }

        return acc;
      }, newSeats);

      return {
        ...currentState,
        seating: { ...seating, newSeats: seatsAfterChange }
      };
    });
  };

  const onSelectNewSeats = (legId) => (seatNumber, availableSeats) => {
    setState((currentState) => {
      const { isMoveAll, isMoveBatch, seating, currentCoach } = currentState;

      if ((isMoveAll || isMoveBatch) && !seating.selectedTempSeat) {
        const selectedLeg = findSelectedLeg(legId, seating.newSeats);
        const passengersForMove =
          selectedLeg?.seats.filter(({ coach, passenger }) =>
            isMoveBatch ? seating.selectedPassengers.some(({ id }) => id === passenger.id) : Number(coach) === Number(seating.tempCoach)
          ) ?? [];
        const filteredSeats = [...availableSeats].sort((a, b) => a.number - b.number);

        const startSeats = filteredSeats.findIndex((seat) => Number(seat.number) === Number(seatNumber));

        let tempSeats = [];
        let tempReversedSeats = [];

        if (passengersForMove?.length > 0 && startSeats >= 0 && startSeats + passengersForMove.length <= filteredSeats.length) {
          tempSeats = passengersForMove.map((passengerForMove, index) => ({
            ...passengerForMove,
            coach: String(currentCoach),
            number: filteredSeats[startSeats + index].number
          }));
        }

        if (passengersForMove?.length > 0 && startSeats >= 0 && startSeats - passengersForMove.length >= 0) {
          tempReversedSeats = passengersForMove.map((passengerForMove, index) => ({
            ...passengerForMove,
            coach: String(currentCoach),
            number: filteredSeats[startSeats - index].number
          }));
        }

        return {
          ...currentState,
          seating: {
            ...seating,
            selectedTempSeat: seatNumber,
            tempSeats,
            tempReversedSeats,
            tempLegId: legId
          }
        };
      }

      return currentState;
    });
  };

  const onSelectNewPassenger = (passengerId) => {
    const { legId, seating, isMoveAll, isMoveBatch } = stateRef.current;
    const selectedPassenger = passengers.find((passengerItem) => passengerItem.id === passengerId);
    const selectedPassengers = [...seating.selectedPassengers, selectedPassenger];

    const [segment] = findSelectedPassengersSegments(legId, selectedPassengers, {
      outbound,
      inbound
    });

    if (segment && segment.id !== legId) {
      setTimeout(() => {
        setSegment(segment.id);
        onSelectNewPassenger(passengerId);
      });

      return;
    }

    if (isMoveBatch) {
      setState((currentState) => ({
        ...currentState,
        seating: {
          ...currentState.seating,
          selectedPassengers: [...currentState.seating.selectedPassengers, selectedPassenger]
        }
      }));
    } else if (!isMoveAll) {
      setState((currentState) => ({
        ...currentState,
        seating: { ...currentState.seating, selectedPassenger }
      }));
    }
  };

  const onRemovePassengerSelection = (passengerId) => {
    setState((currentState) => {
      const { seating, isMoveBatch, isMoveAll } = currentState;

      if (isMoveBatch) {
        return {
          ...currentState,
          seating: {
            ...seating,
            selectedPassengers: seating.selectedPassengers.filter(({ id }) => id !== passengerId)
          }
        };
      }

      if (!isMoveAll) {
        return {
          ...currentState,
          seating: { ...seating, selectedPassenger: null }
        };
      }

      return currentState;
    });
  };

  const onShowUnavailableSeatReason = (seatNumber, seatsData) => {
    const seatStatus = seatsData?.find(({ seat }) => seat === seatNumber) ?? {};
    setState((currentState) => ({ ...currentState, showUnavailable: true, seatStatus }));
  };

  const selectCarriage = (carriageNumber) => {
    setState((currentState) => ({
      ...currentState,
      currentCoach: carriageNumber,
      showUnavailable: false
    }));
  };

  const selectMove = (isSelectAll) => () => {
    setState((currentState) => {
      const { isMoveAll, isMoveBatch, currentCoach, seating } = currentState;

      return {
        ...currentState,
        ...(isSelectAll ? { isMoveAll: !isMoveBatch && !isMoveAll } : { isMoveBatch: !isMoveAll && !isMoveBatch }),
        seating: {
          ...seating,
          tempCoach: currentCoach,
          isReversed: false,
          tempSeats: [],
          tempReversedSeats: [],
          tempLegId: null,
          selectedTempSeat: null,
          selectedPassenger: null,
          selectedPassengers: []
        }
      };
    });
  };

  const changeReverse = () =>
    setState((currentState) => ({
      ...currentState,
      seating: { ...currentState.seating, isReversed: !currentState.seating.isReversed }
    }));

  const cancelMove = () => {
    setState((currentState) => ({
      ...currentState,
      isMoveAll: false,
      isMoveBatch: false,
      seating: {
        ...currentState.seating,
        isReversed: false,
        tempSeats: [],
        tempReversedSeats: [],
        tempCoach: null,
        tempLegId: null,
        selectedTempSeat: null,
        selectedPassenger: null,
        selectedPassengers: []
      }
    }));
  };

  const selectNewStartingPoint = () =>
    setState((currentState) => ({
      ...currentState,
      seating: {
        ...currentState.seating,
        isReversed: false,
        tempSeats: [],
        tempReversedSeats: [],
        tempLegId: null,
        selectedTempSeat: null
      }
    }));

  const confirmMoving = () => {
    setState((currentState) => {
      const {
        seating,
        seating: { newSeats, tempLegId, tempSeats, tempReversedSeats, isReversed }
      } = currentState;

      const seatsForMove = isReversed ? tempReversedSeats : tempSeats;

      const seatsAfterChange = directions.reduce((acc, direction) => {
        const leg = newSeats[direction].find(({ id }) => id === tempLegId);

        if (leg) {
          return {
            ...acc,
            [direction]: findAndReplace(propEq('id', tempLegId), {
              id: tempLegId,
              seats: leg.seats.map((seat) => {
                const newPassenger = seatsForMove.find(({ passenger }) => passenger.id === seat.passenger.id);

                if (newPassenger) {
                  return newPassenger;
                }

                return seat;
              })
            })(acc[direction])
          };
        }

        return acc;
      }, newSeats);

      return {
        ...currentState,
        seating: {
          ...seating,
          newSeats: seatsAfterChange,
          isReversed: false,
          tempSeats: [],
          tempReversedSeats: [],
          tempCoach: null,
          tempLegId: null,
          selectedTempSeat: null,
          selectedPassenger: null,
          selectedPassengers: []
        },
        isMoveAll: false,
        isMoveBatch: false
      };
    });
  };

  const setSegment = (segmentId) => {
    setState((currentState) => {
      const {
        seating,
        seating: {
          newSeats: { inbound: currentInbound, outbound: currentOutbound }
        }
      } = currentState;

      const selectedSeats = [...currentOutbound, ...currentInbound].find(({ id }) => id === segmentId);

      const {
        seats,
        seats: [{ coach: currentCoach }]
      } = selectedSeats;

      const currentInventoryClasses = getCurrentInventoryClasses(seats);

      return {
        ...currentState,
        legId: segmentId,
        currentCoach,
        currentInventoryClasses,
        isMoveAll: false,
        seating: {
          ...seating,
          selectedPassenger: null,
          isReversed: false,
          tempSeats: [],
          tempReversedSeats: [],
          tempCoach: null,
          tempLegId: null,
          selectedTempSeat: null
        }
      };
    });
  };

  const revertSeatSelection = () => {
    setState((currentState) => ({
      ...currentState,
      seating: {
        ...currentState.seating,
        newSeats: currentState.seating.originalSeats
      }
    }));
  };

  const handleUpdateSeatsSelection = useCallback(async (updateSeats) => {
    await updateSeats();
    setState((currentState) => ({
      ...currentState,
      seating: {
        ...currentState.seating,
        originalSeats: currentState.seating.newSeats
      }
    }));
  }, []);

  const handleSeatsWithBooking = ({ unavailableSeatsWithBookingsForCoach }) => {
    setState((currentState) => ({
      ...currentState,
      seatsWithBookings: unavailableSeatsWithBookingsForCoach
    }));
  };

  const handleSeatsWithoutBooking = ({ unavailableSeatsWithoutBookings }) => {
    setState((currentState) => ({
      ...currentState,
      seatsWithoutBookings: unavailableSeatsWithoutBookings
    }));
  };

  const {
    legId,
    visibleLegIds,
    currentCoach,
    currentInventoryClasses,
    seating: { newSeats, selectedPassenger, selectedPassengers, originalSeats, tempSeats, selectedTempSeat, tempReversedSeats, isReversed },
    isMoveAll,
    isMoveBatch,
    seatStatus,
    showUnavailable,
    seatsWithBookings,
    seatsWithoutBookings
  } = state;

  const {
    bookingData: { reference, status, pendingCancellationLegs, pendingCreationLegs, productFamilyGroup },
    children,
    onCompleted,
    isMYB,
    onSeatsUpdate,
    user
  } = props;

  const hasSeats = Boolean(originalSeats.outbound.length || originalSeats.inbound.length);
  const isOnHold = !isConfirmed(status);

  if (!hasSeats) {
    return children({
      actions: {
        updatePassengerSeats: onCompleted
      }
    });
  }

  const seatsToUpdate = findSeatsToUpdate(originalSeats, newSeats);

  const journey = findSelectedLeg(legId, { outbound, inbound });

  const selectedSeatsForSelectedLeg = findSelectedTrainSeats(legId, newSeats);
  const originalSeatsForSelectedLeg = findSelectedTrainSeats(legId, originalSeats);
  const currentSeats = [
    ...new Map(
      [...selectedSeatsForSelectedLeg, ...(isReversed ? tempReversedSeats : tempSeats)].map((item) => [item.passenger.id, item])
    ).values()
  ];

  const canMoveAllSeats =
    passengers.every(({ disabilityType }) => disabilityType !== types.WHEELCHAIR && disabilityType !== types.COMPANION) &&
    !isMissedDeparture &&
    !isFlowForwardCapped;

  const canConfirmMoving = (isReversed && tempReversedSeats?.length > 0) || (!isReversed && tempSeats?.length > 0);
  const canUpdateSeats = !originalSeatsForSelectedLeg.every((originalSeat) =>
    currentSeats.some((currentSeat) => Number(originalSeat.number) === Number(currentSeat.number))
  );

  const tabLegs = [...outbound, ...inbound].filter((leg) =>
    visibleLegIds ? visibleLegIds.some((visibleLegId) => visibleLegId === leg.id) : true
  );

  const selectedIdIsGreenspeedTrain = journey?.serviceType?.code === serviceTypeCodes.GREENSPEED;
  const shouldFetchAssistancesPerCoaches = shouldFetchBookedAssistancesPerCoaches({
    productFamily: productFamilyGroup,
    pendingCreationLegs,
    pendingCancellationLegs,
    direction: journey?.direction,
    isMYB: isMYB
  });

  const renderedSeatSelection = (updatePassengerSeats) => (
    <>
      <StyledTabContainer
        id="seat-selection-tabs"
        data-testid="seat-selection-tabs"
        selectedIdIsGreenspeedTrain={selectedIdIsGreenspeedTrain}
      >
        {tabLegs.map(
          ({
            destination: { name: destinationName, shortCode: destinationCode },
            id,
            origin: { name: originName, shortCode: originCode },
            timing: { departs, isoDate }
          }) => (
            <StyledTab
              type="button"
              key={id}
              selected={id === legId}
              onClick={() => setSegment(id)}
              id={`seat-selection-tab_${id}`}
              data-testid={`seat-selection-tab_${id}`}
              data-selected={id === legId}
              selectedIdIsGreenspeedTrain={selectedIdIsGreenspeedTrain}
            >
              <LegRoute>
                <LegRouteOrigin
                  data-for="seat-selection-tabs-tooltip"
                  data-tip={originName}
                  data-testid={`seat-selection-tab_${id}_origin`}
                >
                  {originCode}
                </LegRouteOrigin>
                <LegRouteDestination
                  data-for="seat-selection-tabs-tooltip"
                  data-tip={destinationName}
                  data-testid={`seat-selection-tab_${id}_destination`}
                >
                  {destinationCode}
                </LegRouteDestination>
              </LegRoute>
              <LegDate>
                {moment.utc(isoDate).format('DD MMM YYYY')} <LegTime>at {departs}</LegTime>
              </LegDate>
            </StyledTab>
          )
        )}
        <ReactTooltip id="seat-selection-tabs-tooltip" data-cy="seat-selection-tabs-tooltip" />
      </StyledTabContainer>
      <TrainMap
        journey={journey}
        selectCarriage={selectCarriage}
        currentCoach={currentCoach}
        currentInventoryClasses={currentInventoryClasses}
        nextAvailableTrain={(isMissedDeparture || isFlowForwardCapped) && nextAvailableTrain}
        shouldFetchAssistancesPerCoaches={shouldFetchAssistancesPerCoaches}
        data-testid="seat-selection-trainmap"
      />
      <SeatMap
        journey={journey}
        currentCoach={currentCoach}
        seats={currentSeats}
        originalSeats={originalSeatsForSelectedLeg}
        selectedPassenger={selectedPassenger}
        selectedPassengers={selectedPassengers}
        onSelectNewPassenger={onSelectNewPassenger}
        onRemovePassengerSelection={onRemovePassengerSelection}
        onShowUnavailableSeatReason={onShowUnavailableSeatReason}
        onSelectNewSeat={onSelectNewSeat}
        onSelectNewSeats={onSelectNewSeats}
        currentInventoryClasses={currentInventoryClasses}
        nextAvailableTrain={(isMissedDeparture || isFlowForwardCapped) && nextAvailableTrain}
        data-testid="seat-selection-seatmap"
        isERTrain={selectedIdIsGreenspeedTrain}
        showUnavailableSeatsReason={isMYB && isEurostarOrganization(user)}
        reference={reference}
        showUnavailable={showUnavailable}
        seatStatus={seatStatus}
        sendSeatsWithBooking={handleSeatsWithBooking}
        sendSeatsWithoutBooking={handleSeatsWithoutBooking}
      />
      <SeatMapLegend isERTrain={selectedIdIsGreenspeedTrain} />
      {isMYB && isEurostarOrganization(user) && (
        <UnavailableSeat
          currentCoach={currentCoach}
          reference={reference}
          unavailableSeatsWithBookings={seatsWithBookings}
          unavailableSeatsWithoutBookings={seatsWithoutBookings}
        />
      )}
      {canMoveAllSeats ? (
        <MoveContainer>
          <MoveCheckboxesContainer>
            <Checkbox
              dataCy="move-batch-checkbox"
              onChange={selectMove(false)}
              checked={isMoveBatch}
              label={<FormattedMessage id="paxForm.batchMove" />}
            />
            <Checkbox
              dataCy="move-all-checkbox"
              onChange={selectMove(true)}
              checked={isMoveAll}
              label={<FormattedMessage id="paxForm.moveAll" />}
            />
          </MoveCheckboxesContainer>
          <MoveActionsContainer>
            <MoveButtonsContainer data-cy="move-buttons" data-testid="move-buttons">
              {canConfirmMoving ? (
                <ConfirmButton type="button" onClick={confirmMoving} data-testid="confirm-button">
                  <FormattedMessage id="bookingDetails.confirm" />
                </ConfirmButton>
              ) : null}
              {selectedTempSeat ? (
                <>
                  <ActionButton type="button" onClick={selectNewStartingPoint}>
                    <FormattedMessage id="paxForm.newStartingPoint" />
                  </ActionButton>
                  <ActionButton type="button" onClick={changeReverse}>
                    <FormattedMessage id="paxForm.reverseOrder" />
                  </ActionButton>
                  <CancelButton type="button" onClick={cancelMove}>
                    <FormattedMessage id="manageBooking.cancel" />
                  </CancelButton>
                </>
              ) : null}
            </MoveButtonsContainer>
            {selectedTempSeat && !canConfirmMoving ? (
              <ErrorMessage data-testid="cannot-move-error" data-cy="cannot-move-error">
                <FormattedMessage id="paxForm.cannotConfirmSeats" />
              </ErrorMessage>
            ) : null}
          </MoveActionsContainer>
        </MoveContainer>
      ) : null}
      {canUpdateSeats && (
        <UpdateSeatsContainer data-cy="selection-buttons">
          <ConfirmButton type="button" onClick={() => handleUpdateSeatsSelection(updatePassengerSeats)} data-testid="update-cta">
            <FormattedMessage id="paxForm.updateSeatsSelection" />
          </ConfirmButton>
          <ActionButton type="button" onClick={revertSeatSelection} data-testid="revert-cta">
            <FormattedMessage id="paxForm.revertSeatSelection" />
          </ActionButton>
        </UpdateSeatsContainer>
      )}
    </>
  );

  return (
    <UpdatePassengerSeatsMutation onCompleted={onCompleted} isNewBooking={isNewBooking} isOnHold={isOnHold}>
      {(updatePassengerSeats, { loading: isUpdatingSeats }) => {
        const updateSeats = onSeatsUpdate ? () => onSeatsUpdate(seatsToUpdate) : updatePassengerSeats.bind(null, reference, seatsToUpdate);

        return children({
          renderedSeatSelection: renderedSeatSelection(updateSeats),
          legId: legId,
          selectedPassenger,
          selectedPassengers,
          selectedSeats: selectedSeatsForSelectedLeg,
          areNewSeatsSelected: seatsToUpdate.length > 0,
          isUpdatingSeats,
          disabledContinue: Boolean(selectedTempSeat),
          journey,
          actions: {
            onSelectNewPassenger,
            onRemovePassengerSelection,
            updatePassengerSeats: updateSeats
          }
        });
      }}
    </UpdatePassengerSeatsMutation>
  );
};

SeatSelection.propTypes = {
  bookingData: bookingShape.isRequired,
  outbound: legsShape,
  inbound: legsShape,
  isNewBooking: PropTypes.bool,
  legId: PropTypes.string,
  visibleLegIds: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.func.isRequired,
  onSeatsUpdate: PropTypes.func,
  onCompleted: PropTypes.func,
  isMissedDeparture: PropTypes.bool,
  isFlowForwardCapped: PropTypes.bool,
  nextAvailableTrain: nextAvailableTrainShape,
  selectedPassengerIds: PropTypes.arrayOf(PropTypes.string),
  isMYB: PropTypes.bool,
  user: userShape
};

export default SeatSelection;
